import React, {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import styled, {css} from 'styled-components';
import {useDispatch, useSelector} from 'react-redux';
import {Auth, Hub} from 'aws-amplify';

import store from './../../store';
import {mobileMediaQuery, tabletMediaQuery, useBreakpoint} from '../../styles';

import Button from '../Button';
import {CartoonitoLink} from '../CartoonitoLink';

import {ReactComponent as CloseIcon} from '../../assets/close.svg';
import Favourites from '../../assets/favourites_icon.png';
import KeyIcon from '../../assets/key_icon.svg';
import ExitIcon from '../../assets/icon-exit.png';

function DrawerMenu({defaultState, callback}) {
    const {t} = useTranslation();

    const dispatch = useDispatch();
    const breakpoint = useBreakpoint();

    const isMobile = ['xs', 'mobile'].includes(breakpoint);
    const isTablet = ['sm', 'md'].includes(breakpoint);

    const bodyElement = document.querySelector('body');

    const user = useSelector(state => state.user);

    const idp = user && user.idp;

    const [federatedSignInProvider, setFederatedSignInProvider] = useState({
        provider: 'WMID',
    });

    const [open, setOpen] = useState(defaultState || false);

    useEffect(() => {
        Hub.listen('auth', data => {
            let config = store.getState().config;

            setFederatedSignInProvider(
                config.auth_settings &&
                    config.auth_settings.oidc &&
                    config.auth_settings.oidc.federatedSignIn
                    ? config.auth_settings.oidc.federatedSignIn
                    : {
                          provider: 'WMID',
                      },
            );
        });

        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (open !== defaultState) {
            setOpen(defaultState);
        }

        // eslint-disable-next-line
    }, [defaultState]);

    useEffect(() => {
        if (open) {
            let top = window.scrollY;

            bodyElement.style.position = 'fixed';
            bodyElement.style.top = `-${top}px`;
            bodyElement.style.left = '0px';
            bodyElement.style.right = '0px';
        } else {
            const scrollY = bodyElement.style.top;

            bodyElement.style.position = '';
            bodyElement.style.top = '';
            bodyElement.style.left = '';
            bodyElement.style.right = '';

            window.scrollTo(0, -parseInt(scrollY || '0'));
        }

        // eslint-disable-next-line
    }, [open]);

    const handleCloseMenu = () => {
        setOpen(false);
        callback && callback(false);
    };

    const handleLogout = () => {
        handleCloseMenu();

        dispatch({
            type: 'MODAL_SHOW',
            modal: {
                visible: true,
                title: t('logout.message'),
                buttons: (
                    <>
                        <Button
                            margin={
                                isMobile || isTablet
                                    ? '0 1rem 0 auto'
                                    : '0 1rem 0 auto'
                            }
                            onClick={handleConfirmLogout}
                        >
                            {t('logout.confirm')}
                        </Button>
                        <Button
                            margin={(isMobile || isTablet) && '0 auto 0 0'}
                            onClick={() => dispatch({type: 'MODAL_CLOSE'})}
                        >
                            {t('logout.cancel')}
                        </Button>
                    </>
                ),
            },
        });
    };

    const handleConfirmLogout = () => {
        dispatch({type: 'MODAL_CLOSE'});

        localStorage.clear();

        Auth.signOut()
            .then(() => {
                localStorage.clear();
                window.location.replace('/');
            })
            .catch(() => {
                localStorage.clear();
                window.location.replace('/');
            });
    };

    const handleLogin = () => {
        handleCloseMenu();

        window.turner_metadata.trackAction.push({
            type: 'interaction',
            data: {
                interaction: 'cnplus|login start',
                domain: 'cnplus.com',
                brand: 'cartoon network',
                interactionevent: '1',
                authstate:
                    user && user.jwtToken ? 'authenticated' : 'unauthenticated',
                mvpd: idp || 'idp not loaded',
            },
        });

        if (federatedSignInProvider !== undefined) {
            setTimeout(() => {
                Auth.federatedSignIn(federatedSignInProvider);
            }, 1500);
        }
    };

    return (
        <Container open={open}>
            <Backdrop open={open} />

            <Menu open={open}>
                <CloseButton onClick={handleCloseMenu}>
                    <CloseIcon title={t('controls.close')} />
                </CloseButton>
                <SideBlob src={require('../../assets/blob_drawer.png')}/>
                <li>
                    {!user.jwtToken ? (
                        <LinkButton onClick={handleLogin}>
                            <img
                                alt={t('lockedContent.drawer.cta')}
                                src={KeyIcon}
                                width="22px"
                                height="22px"
                            />
                            Login
                        </LinkButton>
                    ) : (
                        <>
                            <Link to="/favourites" onClick={handleCloseMenu}>
                                <img
                                    alt={t('favorites.title')}
                                    src={Favourites}
                                />
                                {t('favorites.title')}
                            </Link>
                        </>
                    )}
                </li>

                {['xs', 'mobile', 'sm', 'md'].includes(breakpoint) && (
                    <CartoonitoLink />
                )}

                {user.jwtToken && (
                    <LinkButton onClick={handleLogout}>
                        <img
                            alt={t('drawer.logout')}
                            src={ExitIcon}
                            width="22px"
                            height="22px"
                        />
                        {t('drawer.logout')}
                    </LinkButton>
                )}
            </Menu>
        </Container>
    );
}

const Container = styled.aside`
    position: fixed;
    top: 0px;
    right: 0px;
    bottom: 0px;
    left: 0px;
    z-index: 999;
    opacity: 0;
    transition: 0.3s all ease-in-out;
    pointer-events: none;

    ${({open}) =>
        open &&
        css`
            opacity: 1;
            pointer-events: initial;
        `}
`;

const Backdrop = styled.div`
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.85);

    transition: 0.3s all ease-in-out;

    ${({open}) => open && css``}
`;

const CloseButton = styled.button`
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 0px;
    right: 0px;
    margin: 28px 44px 0 0;

    height: 20px;
    width: 20px;
    box-sizing: content-box;

    ${tabletMediaQuery} {
        margin: 28px 44px 0 0;
    }

    ${mobileMediaQuery} {
        margin: 28px 28px 0 0;
    }

    border: none;
    background-color: transparent;
    padding: 0;
    cursor: pointer;

    svg {
        width: 20px;
        height: 20px;
        g {
            fill: #a8a4a8;
        }
    }
`;

const Menu = styled.nav`
    position: fixed;
    top: 0px;
    right: -318px;
    background: rgb(0, 0, 0);
    height: 100%;
    width: 318px;
    max-width: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    padding: 130px 32px 56px 32px;

    transition: 0.3s all ease-in-out;

    ${mobileMediaQuery} {
        padding: 96px 32px 56px 32px;
    }

    ${({open}) =>
        open &&
        css`
            right: 0px;
        `}

    li {
        width: 100%;
        list-style: none;

        a,
        button {
            text-decoration: none;
            display: flex;
            align-items: center;
            justify-content: flex-start;
            width: 100%;
            margin: 0;
            padding-bottom: 16px;
            border-bottom: 1px solid rgb(58, 51, 58);
            padding: none;
            color: #fff;
            font-size: 18px;
            font-weight: 600;
            line-height: 31px;

            ${mobileMediaQuery} {
                font-size: 16px;
            }

            img {
                object-fit: contain;
                width: 28px;
                height: 28px;
                margin-right: 14px;
            }

            :not(:first-child) {
                padding-top: 16px;
            }
        }

        :first-of-type {
            margin-bottom: 40px;
        }

        :last-of-type {
            a {
                font-size: 14px;
                font-weight: initial;
                height: 24px;
                line-height: 24px;
                text-decoration: underline;
                width: auto;
                border: none;
                padding: 0;

                :not(:last-child) {
                    margin-bottom: 12px;
                }
            }
        }
    }
`;

export const LinkButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    background-color: transparent;
    border: none;
    padding: 0;
    margin: 0;
    margin-top: auto;
    padding: none;
    color: #fff;
    font-size: 18px;
    font-weight: 600;
    line-height: 31px;
    cursor: pointer;

    ${mobileMediaQuery} {
        font-size: 16px;
    }

    img {
        object-fit: contain;
        width: 28px;
        height: 28px;
        margin-right: 14px;
    }
`;

const SideBlob = styled.img`
    position: absolute;
    right: 0px;
    top: 34px;
`;

export default DrawerMenu;
